import React, { useEffect, useMemo, useState } from "react";
import ModalShell from "./ModalShell";
import PhoneInput from "./PhoneInput";
import GenderSelect from "./GenderSelect";
import CheckboxMultiSelect from "./CheckboxMultiSelect";
import { validateEmail, validatePhone } from "../shared/utils/validation";
import { formatPhoneDisplay } from "../shared/utils/phoneFormat";
import { getGenderDisplayLabel, normalizeGenderForForm, validateRequiredGender } from "../shared/utils/genderOptions";
const { displayEmailOrDash } = require("../shared/utils/displayEmail");

/**
 * Labeled form field with optional click-to-edit (edit mode).
 * Must stay at module scope — defining this inside UserAdminModal remounts
 * inputs on every keystroke and steals focus after one character.
 */
function FieldBlock({
  field,
  label,
  required,
  readText,
  children,
  isEditMode,
  activeEditField,
  setActiveEditField,
  submitting,
}) {
  const editing = !isEditMode || activeEditField === field;
  const showRead = isEditMode && !editing;
  const idBase = `user-admin-modal-${field}`;
  const readControlId = `${idBase}-read`;
  return (
    <div className="form-field" style={{ minWidth: 0 }}>
      <label htmlFor={editing ? idBase : readControlId}>
        {label}
        {required ? " *" : ""}
      </label>
      {showRead ? (
        <button
          type="button"
          id={readControlId}
          className={`ai-preview-read${!readText || readText === "—" ? " ai-preview-read-muted" : ""}`}
          disabled={submitting}
          onClick={() => !submitting && setActiveEditField(field)}
        >
          {readText || "—"}
        </button>
      ) : (
        children
      )}
    </div>
  );
}

/**
 * Create or edit a user (shared form for User Admin).
 * Parent owns API calls via onSubmit.
 *
 * - Create: labeled fields, always editable.
 * - Edit: labeled fields, click-to-edit (read row → focus control, blur returns to read).
 *
 * @param {(payload: { mode, _id?, first_name, last_name, email_id, phone_number, gender, roles }) => Promise<void>} onSubmit
 */
function UserAdminModal({ open, mode, user, roleObjs, onCancel, onSubmit }) {
  const [first_name, setFirstName] = useState("");
  const [last_name, setLastName] = useState("");
  const [email_id, setEmailID] = useState("");
  const [phone_number, setPhoneNumber] = useState("");
  const [gender, setGender] = useState("");
  const [roles, setRoles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  /** Edit mode only: which field is actively being edited (null = all read). */
  const [activeEditField, setActiveEditField] = useState(null);

  const isEditMode = mode === "edit";

  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setError("");
    setActiveEditField(null);
    if (mode === "edit" && user) {
      const rolesArr =
        user.roles && user.roles.length ? user.roles : user.role ? [user.role] : [];
      setFirstName(user.first_name ?? "");
      setLastName(user.last_name != null ? user.last_name : "");
      setEmailID(user.email_id || "");
      setPhoneNumber(user.phone_number || "");
      setGender(normalizeGenderForForm(user.gender));
      setRoles([...rolesArr]);
    } else {
      setFirstName("");
      setLastName("");
      setEmailID("");
      setPhoneNumber("");
      setGender("");
      setRoles([]);
    }
  }, [open, mode, user]);

  const formFilled =
    Boolean(
      (first_name || "").trim() &&
        (last_name || "").trim() &&
        (email_id || "").trim() &&
        (phone_number || "").trim() &&
        (gender || "").trim() &&
        Array.isArray(roles) &&
        roles.length > 0
    );

  const rolesSummary =
    Array.isArray(roles) && roles.length
      ? roles.filter((r) => r != null && String(r).trim()).join(", ")
      : "—";

  const roleOptions = useMemo(
    () =>
      (roleObjs || [])
        .map((roleObj) => {
          const role = roleObj && roleObj.role != null ? String(roleObj.role).trim() : "";
          return role ? { value: role, label: role } : null;
        })
        .filter(Boolean),
    [roleObjs]
  );

  const blurIfEdit = () => {
    if (isEditMode) setActiveEditField(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!onSubmit || submitting) return;

    const firstTrim = (first_name != null ? String(first_name) : "").trim();
    const lastTrim = (last_name != null ? String(last_name) : "").trim();
    const rolesTrim = Array.isArray(roles)
      ? roles.filter((r) => r != null && String(r).trim()).map((r) => String(r).trim())
      : [];

    if (!firstTrim) {
      setError("First name is required");
      return;
    }
    if (!lastTrim) {
      setError("Last name is required");
      return;
    }
    const emailCheck = validateEmail(email_id);
    if (!emailCheck.valid) {
      setError(emailCheck.message);
      return;
    }
    const phoneCheck = validatePhone(phone_number);
    if (!phoneCheck.valid) {
      setError(phoneCheck.message);
      return;
    }
    if (rolesTrim.length === 0) {
      setError("At least one role is required");
      return;
    }
    const genderErr = validateRequiredGender(gender);
    if (genderErr) {
      setError(genderErr);
      return;
    }

    const genderTrim = String(gender).trim();

    const payload = {
      mode,
      _id: mode === "edit" && user ? user._id : undefined,
      first_name: firstTrim,
      last_name: lastTrim,
      email_id: String(email_id || "").trim(),
      phone_number,
      gender: genderTrim,
      roles: rolesTrim,
    };

    setSubmitting(true);
    setError("");
    try {
      await onSubmit(payload);
    } catch (err) {
      const msg = err && typeof err.message === "string" ? err.message.trim() : "";
      if (msg) setError(msg);
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  const title = mode === "edit" ? "Edit user" : "New user";
  const submitLabel = mode === "edit" ? "Save" : "Create";
  const subtitle = isEditMode
    ? "Click a field to edit. First name, last name, phone, and at least one role are required. Gender is optional. To change email, use Swap email on the user row."
    : "Enter the details below. First name, last name, email, phone, and at least one role are required. Gender is optional.";

  return (
    <ModalShell
      open={open}
      onClose={submitting ? undefined : onCancel}
      title={title}
      subtitle={subtitle}
      maxWidth={560}
      zIndex={1090}
      closeOnOverlayClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={submitting}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="user-admin-modal-form"
            className="btn btn-primary"
            disabled={submitting || (!formFilled && mode === "create")}
          >
            {submitting ? "Saving…" : submitLabel}
          </button>
        </>
      }
    >
      <form id="user-admin-modal-form" className="modal-form" onSubmit={handleSubmit}>
        {error ? (
          <div style={{ color: "#c92a2a", fontSize: 13, marginBottom: 12 }}>{error}</div>
        ) : null}

        <div className="form-row" style={{ alignItems: "start" }}>
          <FieldBlock
            field="first_name"
            label="First name"
            required
            readText={(first_name || "").trim() || "—"}
            isEditMode={isEditMode}
            activeEditField={activeEditField}
            setActiveEditField={setActiveEditField}
            submitting={submitting}
          >
            <input
              id="user-admin-modal-first_name"
              type="text"
              value={first_name}
              onChange={(e) => setFirstName(e.target.value)}
              onBlur={blurIfEdit}
              autoFocus={isEditMode && activeEditField === "first_name"}
              className="input"
              disabled={submitting}
              autoComplete="given-name"
            />
          </FieldBlock>
          <FieldBlock
            field="last_name"
            label="Last name"
            required
            readText={(last_name || "").trim() || "—"}
            isEditMode={isEditMode}
            activeEditField={activeEditField}
            setActiveEditField={setActiveEditField}
            submitting={submitting}
          >
            <input
              id="user-admin-modal-last_name"
              type="text"
              value={last_name}
              onChange={(e) => setLastName(e.target.value)}
              onBlur={blurIfEdit}
              autoFocus={isEditMode && activeEditField === "last_name"}
              className="input"
              disabled={submitting}
              autoComplete="family-name"
            />
          </FieldBlock>
        </div>

        <div className="form-row" style={{ alignItems: "start" }}>
          {isEditMode ? (
            <div className="form-field" style={{ minWidth: 0 }}>
              <label htmlFor="user-admin-modal-email_id-read">Email ID *</label>
              <input
                id="user-admin-modal-email_id-read"
                type="text"
                className="input"
                value={displayEmailOrDash(email_id)}
                disabled
                readOnly
                title="Use Swap email on the user row to change login email"
              />
            </div>
          ) : (
            <FieldBlock
              field="email_id"
              label="Email ID"
              required
              readText={displayEmailOrDash(email_id)}
              isEditMode={isEditMode}
              activeEditField={activeEditField}
              setActiveEditField={setActiveEditField}
              submitting={submitting}
            >
              <input
                id="user-admin-modal-email_id"
                type="text"
                value={email_id}
                onChange={(e) => setEmailID(e.target.value)}
                onBlur={blurIfEdit}
                className="input"
                disabled={submitting}
                autoComplete="email"
              />
            </FieldBlock>
          )}
          <FieldBlock
            field="phone"
            label="Phone number"
            required
            readText={formatPhoneDisplay(phone_number) || "—"}
            isEditMode={isEditMode}
            activeEditField={activeEditField}
            setActiveEditField={setActiveEditField}
            submitting={submitting}
          >
            <PhoneInput
              id="user-admin-modal-phone"
              value={phone_number}
              onChange={setPhoneNumber}
              onBlur={blurIfEdit}
              className="input"
              disabled={submitting}
              autoFocus={isEditMode && activeEditField === "phone"}
              autoComplete="tel"
            />
          </FieldBlock>
        </div>

        <div className="form-row" style={{ alignItems: "start" }}>
          <FieldBlock
            field="gender"
            label="Gender"
            required
            readText={getGenderDisplayLabel(gender)}
            isEditMode={isEditMode}
            activeEditField={activeEditField}
            setActiveEditField={setActiveEditField}
            submitting={submitting}
          >
            <GenderSelect
              id="user-admin-modal-gender"
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              onBlur={blurIfEdit}
              disabled={submitting}
              required
              autoFocus={isEditMode && activeEditField === "gender"}
            />
          </FieldBlock>
          <div className="form-field" style={{ minWidth: 0 }}>
            <span id="user-admin-modal-roles-label" style={{ display: "block", marginBottom: 2, fontSize: "0.8125rem", fontWeight: 600, color: "var(--label-color, #495057)" }}>
              Roles *
            </span>
            {isEditMode && activeEditField !== "roles" ? (
              <button
                type="button"
                id="user-admin-modal-roles-read"
                className={`ai-preview-read${!rolesSummary || rolesSummary === "—" ? " ai-preview-read-muted" : ""}`}
                disabled={submitting}
                onClick={() => !submitting && setActiveEditField("roles")}
                aria-labelledby="user-admin-modal-roles-label"
              >
                {rolesSummary}
              </button>
            ) : (
              <CheckboxMultiSelect
                options={roleOptions}
                value={Array.isArray(roles) ? roles : []}
                onChange={setRoles}
                placeholder="— Select role(s) —"
                listboxLabel="Roles"
                title="Roles"
                disabled={submitting}
              />
            )}
          </div>
        </div>
      </form>
    </ModalShell>
  );
}

export default UserAdminModal;
